import React from "react";
import { Button, Checkbox, Radio } from "antd";
import styles from '../../styles/mobileInfoPage.module.scss';
import quizStyles from '../../styles/quizForm.module.scss';
import {
    FloatingLabelDatePicker,
    FloatingLabelInput,
    FloatingLabelSelect,
} from "../../components/elements/floatingLabelInput/FloatingLabelInput";
import CustomSelect from "../../components/elements/select/Select";
import TranslateButton from "../../components/elements/TranslateButton/TranslateButton";
import { useTranslateFields } from "../../hooks/useTranslateFields";
import dayjs from 'dayjs';
import { toast } from "react-toastify";
import {
    APP_OPEN_PAGE_OPTION,
    DISPLAY_FREQUENCY_OPTIONS,
    DISPLAY_PAGE_OPTIONS,
    EVERY_TIME_FREQUENCY,
    TARGET_USER_GROUP_OPTIONS,
    syncDisplayInPageWithFrequency,
} from "../../utils/announcement.util";
import {
    QUESTION_TYPE_OPTIONS,
    QUIZ_STATUS_OPTIONS,
    WRITTEN_QUIZ_BONUS_POINTS,
    WRITTEN_TEXT_QUESTION_TYPE,
    applySingleChoiceExclusivity,
    emptyQuizOption,
    emptyQuizQuestion,
    quizHasScoredChoiceQuestion,
} from "../../utils/quiz.util";

// Defined at module scope (not inside QuizFormFields) so each question's translate
// button keeps its own independent `translating` state across parent re-renders -
// an inline component here would remount on every keystroke elsewhere in the form.
function QuestionTranslateButton({ question, onTranslated }) {
    const { translating, translate } = useTranslateFields();

    const handleClick = async () => {
        const srText = question.text?.sr?.trim();
        if (!srText) {
            toast.error('Popunite tekst pitanja na srpskom pre prevođenja');
            return;
        }

        const fields = { qText: question.text.sr };
        question.options.forEach((opt, idx) => {
            if (opt.text?.sr?.trim()) {
                fields[`opt${idx}`] = opt.text.sr;
            }
        });

        const result = await translate(fields);
        if (!result) return;

        const updatedOptions = question.options.map((opt, idx) => {
            const key = `opt${idx}`;
            if (result.hu[key] === undefined && result.en[key] === undefined) {
                return opt;
            }
            return {
                ...opt,
                text: {
                    ...opt.text,
                    ...(result.hu[key] !== undefined ? { hu: result.hu[key] } : {}),
                    ...(result.en[key] !== undefined ? { en: result.en[key] } : {}),
                },
            };
        });

        onTranslated({
            ...question,
            text: { ...question.text, hu: result.hu.qText, en: result.en.qText },
            options: updatedOptions,
        });
        toast.success('Prevod je uspešno primenjen');
    };

    return <TranslateButton onClick={handleClick} loading={translating} size="small">Prevedi</TranslateButton>;
}

export default function QuizFormFields({ formData, updateFormData }) {
    const { translating, translate } = useTranslateFields();

    const handleTranslate = async () => {
        if (!formData.title?.sr?.trim()) {
            toast.error('Popunite naslov na srpskom pre prevođenja');
            return;
        }

        const fields = { title: formData.title.sr };
        if (formData.description?.sr?.trim()) {
            fields.description = formData.description.sr;
        }

        const result = await translate(fields);
        if (!result) return;

        updateFormData('title', {
            ...formData.title,
            hu: result.hu.title,
            en: result.en.title,
        });
        if (result.hu.description !== undefined || result.en.description !== undefined) {
            updateFormData('description', {
                ...formData.description,
                ...(result.hu.description !== undefined ? { hu: result.hu.description } : {}),
                ...(result.en.description !== undefined ? { en: result.en.description } : {}),
            });
        }

        toast.success('Prevod je uspešno primenjen');
    };

    React.useEffect(() => {
        syncDisplayInPageWithFrequency(formData, updateFormData);
    }, [formData.displayFrequency]);

    const updateQuestion = (qIndex, updatedQuestion) => {
        const questions = formData.questions.map((q, idx) => (idx === qIndex ? updatedQuestion : q));
        updateFormData('questions', questions);
    };

    const addQuestion = () => {
        updateFormData('questions', [...formData.questions, emptyQuizQuestion()]);
    };

    const removeQuestion = (qIndex) => {
        if (formData.questions.length <= 1) {
            toast.error('Kviz mora imati bar jedno pitanje.');
            return;
        }
        updateFormData('questions', formData.questions.filter((_, idx) => idx !== qIndex));
    };

    const addOption = (qIndex) => {
        const question = formData.questions[qIndex];
        updateQuestion(qIndex, { ...question, options: [...question.options, emptyQuizOption()] });
    };

    const removeOption = (qIndex, oIndex) => {
        const question = formData.questions[qIndex];
        if (question.options.length <= 2) {
            toast.error('Pitanje mora imati bar 2 ponuđena odgovora.');
            return;
        }
        updateQuestion(qIndex, { ...question, options: question.options.filter((_, idx) => idx !== oIndex) });
    };

    const updateOptionText = (qIndex, oIndex, lang, value) => {
        const question = formData.questions[qIndex];
        const options = question.options.map((opt, idx) =>
            idx === oIndex ? { ...opt, text: { ...opt.text, [lang]: value } } : opt,
        );
        updateQuestion(qIndex, { ...question, options });
    };

    const onQuestionTypeChange = (qIndex, type) => {
        const question = formData.questions[qIndex];
        let options;
        if (type === WRITTEN_TEXT_QUESTION_TYPE) {
            // Written-text questions carry no answer options at all.
            options = [];
        } else if (question.type === WRITTEN_TEXT_QUESTION_TYPE) {
            // Coming back from written-text - it needs at least 2 fresh options.
            options = [emptyQuizOption(), emptyQuizOption()];
        } else if (type === 'single-choice') {
            // Switching to single-choice can leave more than one option marked
            // correct from when it was multiple-choice - clear all but keep intent
            // explicit rather than guessing which one to keep.
            options = question.options.map((opt) => ({ ...opt, isCorrect: false }));
        } else {
            options = question.options;
        }
        updateQuestion(qIndex, { ...question, type, options });
    };

    const onOptionCorrectChange = (qIndex, oIndex, checked) => {
        const question = formData.questions[qIndex];
        if (question.type === 'single-choice') {
            updateQuestion(qIndex, applySingleChoiceExclusivity(question, checked ? oIndex : -1));
        } else {
            const options = question.options.map((opt, idx) =>
                idx === oIndex ? { ...opt, isCorrect: checked } : opt,
            );
            updateQuestion(qIndex, { ...question, options });
        }
    };

    return (
        <>
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Osnovne informacije</h3>
                <div className={styles.fieldStack} style={{ flexDirection: 'row', gap: '16px' }}>
                    <FloatingLabelInput
                        label="Interni naziv"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        placeholder="Kviz o zdravim navikama"
                    />
                    <CustomSelect
                        value={QUIZ_STATUS_OPTIONS.find(opt => opt.value === formData.status)}
                        onChange={(value) => updateFormData('status', value)}
                        items={QUIZ_STATUS_OPTIONS}
                        placeholder="Izaberi status"
                    />
                </div>
            </section>

            <section className={styles.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Sadržaj kviza</h3>
                    <TranslateButton onClick={handleTranslate} loading={translating} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <FloatingLabelInput
                        label="Naslov (SR)"
                        value={formData.title?.sr}
                        onChange={(e) => updateFormData('title', { ...formData.title, sr: e.target.value })}
                    />
                    <FloatingLabelInput
                        label="Naslov (HU)"
                        value={formData.title?.hu}
                        onChange={(e) => updateFormData('title', { ...formData.title, hu: e.target.value })}
                    />
                    <FloatingLabelInput
                        label="Naslov (EN)"
                        value={formData.title?.en}
                        onChange={(e) => updateFormData('title', { ...formData.title, en: e.target.value })}
                    />
                    <FloatingLabelInput
                        label="Opis (SR, opciono)"
                        value={formData.description?.sr}
                        onChange={(e) => updateFormData('description', { ...formData.description, sr: e.target.value })}
                    />
                    <FloatingLabelInput
                        label="Opis (HU, opciono)"
                        value={formData.description?.hu}
                        onChange={(e) => updateFormData('description', { ...formData.description, hu: e.target.value })}
                    />
                    <FloatingLabelInput
                        label="Opis (EN, opciono)"
                        value={formData.description?.en}
                        onChange={(e) => updateFormData('description', { ...formData.description, en: e.target.value })}
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Prikaz rezultata</h3>
                <Checkbox
                    checked={formData.showResults}
                    onChange={(e) => updateFormData('showResults', e.target.checked)}
                >
                    Nakon predaje kviza, prikaži korisniku bodove i tačne/netačne odgovore
                </Checkbox>
            </section>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Pitanja</h3>
                {formData.questions.map((question, qIndex) => (
                    <div key={qIndex} className={quizStyles.questionCard}>
                        <div className={quizStyles.questionCardHeader}>
                            <span className={quizStyles.questionCardTitle}>Pitanje {qIndex + 1}</span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <QuestionTranslateButton
                                    question={question}
                                    onTranslated={(updated) => updateQuestion(qIndex, updated)}
                                />
                                <Button
                                    className="btn-tertiary"
                                    danger
                                    onClick={() => removeQuestion(qIndex)}
                                >
                                    Ukloni pitanje
                                </Button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                            <FloatingLabelInput
                                label="Tekst pitanja (SR)"
                                value={question.text?.sr}
                                onChange={(e) => updateQuestion(qIndex, { ...question, text: { ...question.text, sr: e.target.value } })}
                            />
                            <FloatingLabelInput
                                label="Tekst pitanja (HU)"
                                value={question.text?.hu}
                                onChange={(e) => updateQuestion(qIndex, { ...question, text: { ...question.text, hu: e.target.value } })}
                            />
                            <FloatingLabelInput
                                label="Tekst pitanja (EN)"
                                value={question.text?.en}
                                onChange={(e) => updateQuestion(qIndex, { ...question, text: { ...question.text, en: e.target.value } })}
                            />
                        </div>

                        <div style={{ marginBottom: '16px', maxWidth: '360px' }}>
                            <FloatingLabelSelect
                                label="Tip pitanja"
                                value={question.type}
                                onChange={(value) => onQuestionTypeChange(qIndex, value)}
                                items={QUESTION_TYPE_OPTIONS}
                                placeholder="Izaberi tip pitanja"
                            />
                        </div>

                        {question.type === WRITTEN_TEXT_QUESTION_TYPE ? (
                            <p className={styles.fieldLabel}>
                                Korisnik unosi slobodan tekstualni odgovor - nema ponuđenih opcija i odgovor se ne
                                boduje kao tačan/netačan. Popunjavanje ovog pitanja donosi korisniku fiksnih{' '}
                                {WRITTEN_QUIZ_BONUS_POINTS} poena jednom po kvizu (ne po pitanju).
                            </p>
                        ) : (
                            <>
                                <p className={styles.fieldLabel}>Ponuđeni odgovori (označi tačan ako je pitanje bodovano)</p>
                                {question.options.map((option, oIndex) => (
                                    <div key={oIndex} className={quizStyles.optionRow}>
                                        <div className={quizStyles.optionInputs}>
                                            <FloatingLabelInput
                                                label={`Odgovor ${oIndex + 1} (SR)`}
                                                value={option.text?.sr}
                                                onChange={(e) => updateOptionText(qIndex, oIndex, 'sr', e.target.value)}
                                            />
                                            <FloatingLabelInput
                                                label={`Odgovor ${oIndex + 1} (HU)`}
                                                value={option.text?.hu}
                                                onChange={(e) => updateOptionText(qIndex, oIndex, 'hu', e.target.value)}
                                            />
                                            <FloatingLabelInput
                                                label={`Odgovor ${oIndex + 1} (EN)`}
                                                value={option.text?.en}
                                                onChange={(e) => updateOptionText(qIndex, oIndex, 'en', e.target.value)}
                                            />
                                        </div>
                                        <div className={quizStyles.optionCorrectControl}>
                                            {question.type === 'single-choice' ? (
                                                <Radio
                                                    checked={option.isCorrect}
                                                    onChange={(e) => onOptionCorrectChange(qIndex, oIndex, e.target.checked)}
                                                >
                                                    Tačan
                                                </Radio>
                                            ) : (
                                                <Checkbox
                                                    checked={option.isCorrect}
                                                    onChange={(e) => onOptionCorrectChange(qIndex, oIndex, e.target.checked)}
                                                >
                                                    Tačan
                                                </Checkbox>
                                            )}
                                        </div>
                                        <Button
                                            className="btn-tertiary"
                                            onClick={() => removeOption(qIndex, oIndex)}
                                        >
                                            Ukloni
                                        </Button>
                                    </div>
                                ))}
                                <Button className="btn-tertiary" onClick={() => addOption(qIndex)}>
                                    Dodaj odgovor
                                </Button>
                            </>
                        )}
                    </div>
                ))}
                <Button className="btn-primary" onClick={addQuestion}>
                    Dodaj pitanje
                </Button>
            </section>

            {quizHasScoredChoiceQuestion(formData.questions) && (
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Bodovanje kviza</h3>
                    <p className={styles.fieldLabel}>
                        Kviz sadrži bar jedno pitanje sa tačnim odgovorom - odredi koliko procenata tačnih
                        odgovora je potrebno da bi korisnik osvojio poene, i koliko poena nosi svaki tačan odgovor.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '600px' }}>
                        <FloatingLabelInput
                            label="Minimalan % tačnih odgovora za poene"
                            type="number"
                            min={0}
                            max={100}
                            value={formData.minCorrectPercentToEarnPoints}
                            onChange={(e) => updateFormData('minCorrectPercentToEarnPoints', e.target.value === '' ? undefined : Number(e.target.value))}
                            placeholder="npr. 80"
                        />
                        <FloatingLabelInput
                            label="Poena po tačnom odgovoru"
                            type="number"
                            min={1}
                            value={formData.pointsPerCorrectAnswer}
                            onChange={(e) => updateFormData('pointsPerCorrectAnswer', e.target.value === '' ? undefined : Number(e.target.value))}
                            placeholder="npr. 2"
                        />
                    </div>
                </section>
            )}

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Prikaz u aplikaciji</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <FloatingLabelSelect
                        label="Izbor stranice u aplikaciji"
                        value={formData.displayInPage}
                        onChange={(value) => updateFormData('displayInPage', value)}
                        items={formData.displayFrequency === EVERY_TIME_FREQUENCY ? [APP_OPEN_PAGE_OPTION] : DISPLAY_PAGE_OPTIONS}
                        disabled={formData.displayFrequency === EVERY_TIME_FREQUENCY}
                        placeholder="Izaberi stranicu"
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Učestalost prikazivanja</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <FloatingLabelDatePicker
                        label="Start date"
                        value={formData.startDate ? dayjs(formData.startDate) : null}
                        onChange={(date) => updateFormData('startDate', date ? date.toISOString() : null)}
                        format="DD.MM.YYYY."
                        placeholder="29.01.2026."
                    />
                    <FloatingLabelDatePicker
                        label="End date"
                        value={formData.endDate ? dayjs(formData.endDate) : null}
                        onChange={(date) => updateFormData('endDate', date ? date.toISOString() : null)}
                        format="DD.MM.YYYY."
                        placeholder="29.01.2026."
                    />
                    <FloatingLabelSelect
                        label="Frekvencija prikaza"
                        value={formData.displayFrequency}
                        onChange={(value) => updateFormData('displayFrequency', value)}
                        items={DISPLAY_FREQUENCY_OPTIONS}
                        placeholder="Izaberi frekvenciju"
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Targeting korisnika</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <FloatingLabelSelect
                        label="Izaberi grupu korisnika"
                        value={formData.targetUserGroup}
                        onChange={(value) => updateFormData('targetUserGroup', value)}
                        items={TARGET_USER_GROUP_OPTIONS}
                        placeholder="Izaberi grupu"
                    />
                </div>
            </section>
        </>
    );
}
